import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { chime } from "../lib/chime.ts";
import { RestRing } from "./RestRing.tsx";
import { BACK_TO_IT_SECONDS } from "../lib/rest.ts";

/** What the end of a rest says: the next set, now. */
export const BACK_TO_IT = "C'est reparti";

/**
 * Rest timer.
 *
 * It counts against a wall-clock deadline rather than ticking a counter down,
 * so locking the phone or switching apps mid-rest does not freeze it. iOS
 * Safari has no vibration API, so the end of a rest is announced with a short
 * tone and a full-width colour change — something visible from the rack.
 *
 * On a phone it floats up in the corner, the way the session under way does
 * on every other screen: down by the set's button, the phone propped on the
 * bottle holder had it behind the bottle. The screen gives it the room
 * (`REST_PILL_ROOM`) so the way back stays in reach.
 */

/** What a header row leaves free on the right for the rest's pill, below `lg`. */
export const REST_PILL_ROOM = "max-lg:pr-[18.5rem]";

interface RestTimerProps {
  /** Seconds to count down, adjustments included. Restarting with the same value re-arms the timer. */
  seconds: number;
  startedAt: number | null;
  onDone: () => void;
  onDismiss: () => void;
  /** The moment to get back to it is past: the rest closes itself. */
  onExpire: () => void;
  /** ±30 s, kept with the rest itself so every screen showing it agrees. */
  onAdjust: (delta: number) => void;
  label?: string;
}

export function RestTimer({
  seconds,
  startedAt,
  onDone,
  onDismiss,
  onExpire,
  onAdjust,
  label = "Repos",
}: RestTimerProps) {
  const [remaining, setRemaining] = useState(seconds);
  const announced = useRef(false);

  // An effect event, so that passing an inline callback does not restart the
  // countdown on every render.
  const finish = useEffectEvent(() => onDone());
  const expire = useEffectEvent(() => onExpire());

  // A new countdown re-arms the chime.
  useEffect(() => {
    announced.current = false;
  }, [startedAt]);

  const total = seconds;

  useEffect(() => {
    if (startedAt === null) return;

    const tick = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const left = Math.ceil(total - elapsed);
      setRemaining(left);

      if (left <= 0 && !announced.current) {
        announced.current = true;
        chime();
        finish();
      }
      // Back at it by now: no overtime is counted, the rest is over.
      if (left < -BACK_TO_IT_SECONDS) expire();
    };

    tick();
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [startedAt, total]);

  if (startedAt === null || remaining < -BACK_TO_IT_SECONDS) return null;

  const done = remaining <= 0;
  const progress = done ? 1 : 1 - remaining / total;

  // Out: the time to get back to it, in words rather than an overtime.
  const clock = done ? BACK_TO_IT : formatClock(remaining);
  const adjust = !done && (
    <>
      <TimerButton onClick={() => onAdjust(-30)}>−30 s</TimerButton>
      <TimerButton onClick={() => onAdjust(30)}>+30 s</TimerButton>
    </>
  );
  const next = (
    <TimerButton onClick={onDismiss} className={done ? "text-hue" : "text-ink"}>
      {done ? "Série suivante" : "Passer"}
    </TimerButton>
  );

  return (
    <>
      {/* Into the body: the blurred bar it is written in would otherwise be
          what "fixed" is measured against, and it stayed at the bottom. */}
      {createPortal(
        <div
          role="timer"
          aria-label={done ? BACK_TO_IT : label}
          className={[
            "animate-enter fixed right-3 z-40 flex h-12 w-[17.5rem] items-center gap-1 rounded-full border bg-[#0b0d10]/95 pr-1 pl-2 shadow-lg backdrop-blur-lg lg:hidden",
            "top-[calc(env(safe-area-inset-top,0px)+0.5rem)]",
            done ? "border-hue/70" : "border-line/80",
          ].join(" ")}
        >
          <RestRing progress={Math.min(1, progress)} over={done} />
          <span
            className={[
              "num ml-1 leading-none font-bold whitespace-nowrap tabular-nums",
              done ? "text-[0.95rem]" : "text-[1.15rem]",
              done ? "animate-pulse-ring text-hue" : "text-ink",
            ].join(" ")}
          >
            {clock}
          </span>
          <span className="ml-auto flex">
            {adjust}
            {next}
          </span>
        </div>,
        document.body,
      )}
      <RestCard done={done} label={label} clock={clock} adjust={adjust} next={next} progress={progress} />
    </>
  );
}

/** On a wider screen, in the column beside the set: a card the width of the button under it. */
function RestCard({
  done,
  label,
  clock,
  adjust,
  next,
  progress,
}: {
  done: boolean;
  label: string;
  clock: ReactNode;
  adjust: ReactNode;
  next: ReactNode;
  progress: number;
}) {
  return (
    <div
      role="timer"
      aria-label={done ? BACK_TO_IT : label}
      className={[
        "animate-enter overflow-hidden rounded-card border max-lg:hidden",
        done ? "border-hue bg-hue/12" : "border-line bg-surface",
      ].join(" ")}
    >
      {/* One row: under the set's inputs, every line of height counts. */}
      <div className="flex items-center gap-1 py-1.5 pr-1.5 pl-4">
        <span className="text-[0.82rem] text-ink-mute">{done ? "Repos terminé" : label}</span>

        <span
          className={[
            "num ml-2 text-[1.5rem] leading-none font-bold tabular-nums",
            done ? "animate-pulse-ring text-hue" : "text-ink",
          ].join(" ")}
        >
          {clock}
        </span>

        <span className="ml-auto flex">
          {adjust}
          {next}
        </span>
      </div>

      {/* The bar is the timer; the digits only confirm it. */}
      <div className="h-1 w-full bg-surface-high">
        <div
          className={["h-full transition-[width] duration-200 ease-linear", done ? "bg-hue" : "bg-ink/40"].join(" ")}
          style={{ width: `${Math.min(100, progress * 100)}%` }}
        />
      </div>
    </div>
  );
}

function TimerButton({
  children,
  onClick,
  className = "",
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-11 rounded-full px-2.5 text-[0.85rem] font-semibold whitespace-nowrap text-ink-soft active:bg-surface-high ${className}`}
    >
      {children}
    </button>
  );
}

function formatClock(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}
